import { useState } from 'react';
import { Alert, Button, Empty, Segmented, Space, Spin, Table, Tag, Timeline, Typography, type TableColumnsType } from 'antd';
import { Link } from 'react-router-dom';
import { QueryError } from '../../lib/QueryState';
import { LogDetailDrawer } from './LogDetailDrawer';
import {
  SEVERITY_COLOR,
  STATUS_COLOR,
  eventLabel,
  formatGap,
  formatLogTime,
  refName,
  useLogsPage,
  useTimeline,
  type LogRefs,
  type LogRow,
} from './logsApi';

/**
 * Chronological activity for one call / user / listener (oldest first, with the
 * gap since the previous step) — the "what exactly happened" view:
 *
 *   15:30:01  Call requested
 *        2 s
 *   15:30:03  Call ringing        Listener A
 *       10 s
 *   15:30:13  Call declined
 *   …
 */
const DOT: Record<string, string> = { SUCCESS: 'green', FAILURE: 'red', PENDING: 'gray' };

function who(row: LogRow, refs?: LogRefs): string | null {
  const l = refName(refs, 'listener', row.listenerId);
  const u = refName(refs, 'user', row.userId);
  if (row.eventCategory === 'CALL' || row.eventCategory === 'LISTENER') return l ? `Listener ${l}` : null;
  if (row.actorType === 'admin') return refName(refs, 'admin', row.actorId) ? `by ${refName(refs, 'admin', row.actorId)}` : 'by admin';
  return u;
}

/** Short facts worth seeing inline (amount, balance, reason, attempt…). */
function facts(row: LogRow): string[] {
  const m = (row.metadata ?? {}) as Record<string, unknown>;
  const out: string[] = [];
  const add = (label: string, v: unknown) => {
    if (v !== undefined && v !== null && v !== '') out.push(`${label} ${String(v)}`);
  };
  add('attempt', m.attempt_number);
  add('', m.call_type);
  if (m.coin_amount !== undefined) add(m.direction === 'debit' ? '−' : '+', `${String(m.coin_amount)} coins`);
  if (m.amount_paise !== undefined && row.eventCategory === 'WALLET') add(m.direction === 'debit' ? '−' : '+', `${Number(m.amount_paise) / 100} ₹`);
  add('balance', m.balance_after);
  add('', m.end_reason ?? m.reason);
  add('duration', m.duration_seconds != null ? `${String(m.duration_seconds)} s` : null);
  if (row.errorCode) out.push(row.errorCode);
  return out.map((s) => s.trim());
}

export function ActivityTimelineView({ rows, refs, onOpen }: { rows: LogRow[]; refs?: LogRefs; onOpen: (r: LogRow) => void }) {
  if (rows.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No recorded activity yet." />;
  return (
    <Timeline
      items={rows.map((r) => {
        const gap = formatGap(r.sincePreviousMs);
        const w = who(r, refs);
        return {
          key: r.id,
          color: r.severity === 'ERROR' || r.severity === 'CRITICAL' ? 'red' : (DOT[r.status] ?? 'blue'),
          children: (
            <div>
              {gap && (
                <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  + {gap}
                </Typography.Text>
              )}
              <Space size={8} wrap>
                <Typography.Text type="secondary" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                  {formatLogTime(r.createdAt)}
                </Typography.Text>
                <a onClick={() => onOpen(r)}>{eventLabel(r)}</a>
                {r.status !== 'SUCCESS' && <Tag color={STATUS_COLOR[r.status]}>{r.status}</Tag>}
                {w && <Typography.Text style={{ fontSize: 12 }}>{w}</Typography.Text>}
              </Space>
              {(facts(r).length > 0 || r.message) && (
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  {[...facts(r), ...(r.message && r.eventCategory !== 'CALL' ? [r.message] : [])].join(' · ')}
                </Typography.Text>
              )}
            </div>
          ),
        };
      })}
    />
  );
}

/** Call details → "Activity timeline": the call plus its whole fallback request. */
export function CallActivityTimeline({ callId }: { callId: string }) {
  const q = useTimeline({ call_id: callId });
  const [open, setOpen] = useState<LogRow | null>(null);
  if (q.isError) return <QueryError error={q.error} what="the call timeline" onRetry={() => void q.refetch()} />;
  if (q.isLoading) return <Spin size="small" />;
  return (
    <>
      {q.data?.callRequestId && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
          Includes every listener tried for this request (correlation <code>{q.data.callRequestId.slice(0, 8)}</code>).{' '}
          <Link to={`/logs?correlation_id=${encodeURIComponent(q.data.callRequestId)}`}>Open in Logs</Link>
        </Typography.Paragraph>
      )}
      {q.data?.truncated && <Alert type="info" showIcon message="Showing the latest 500 events." style={{ marginBottom: 8 }} />}
      <ActivityTimelineView rows={q.data?.data ?? []} refs={q.data?.refs} onOpen={setOpen} />
      <LogDetailDrawer row={open} refs={q.data?.refs} open={!!open} onClose={() => setOpen(null)} />
    </>
  );
}

/**
 * User / Listener details → "Activity" tab: everything recorded about that
 * person, as a paged table (newest first) or a timeline (oldest first).
 */
export function ActivityLogsTab({ userId, listenerId }: { userId?: string; listenerId?: string }) {
  const [view, setView] = useState<'list' | 'timeline'>('list');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState<LogRow | null>(null);
  const filters = listenerId ? { listener_id: listenerId } : { user_id: userId };
  const list = useLogsPage(filters, page, pageSize, view === 'list' && !!(userId || listenerId));
  const timeline = useTimeline(filters, view === 'timeline' && !!(userId || listenerId));
  const logsLink = `/logs?${listenerId ? `listener_id=${encodeURIComponent(listenerId)}` : `user_id=${encodeURIComponent(userId ?? '')}`}`;

  const columns: TableColumnsType<LogRow> = [
    { title: 'Time', dataIndex: 'createdAt', width: 180, render: (s: string) => formatLogTime(s) },
    { title: 'Event', render: (_: unknown, r) => <a onClick={() => setOpen(r)}>{eventLabel(r)}</a> },
    {
      title: 'Status',
      width: 110,
      render: (_: unknown, r) => (
        <Space size={4}>
          <Tag color={STATUS_COLOR[r.status]}>{r.status}</Tag>
          {r.severity !== 'INFO' && <Tag color={SEVERITY_COLOR[r.severity]}>{r.severity}</Tag>}
        </Space>
      ),
    },
    {
      title: 'Details',
      responsive: ['md'],
      render: (_: unknown, r) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {facts(r).join(' · ') || r.message || ''}
        </Typography.Text>
      ),
    },
  ];

  if (!userId && !listenerId) return <Empty description="No account to show activity for." />;
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
        <Segmented<'list' | 'timeline'>
          size="small"
          value={view}
          onChange={setView}
          options={[
            { value: 'list', label: 'List' },
            { value: 'timeline', label: 'Timeline' },
          ]}
        />
        <Link to={logsLink}>
          <Button size="small" type="link">
            Open in Logs
          </Button>
        </Link>
      </Space>
      {view === 'list' ? (
        list.isError ? (
          <QueryError error={list.error} what="activity" onRetry={() => void list.refetch()} />
        ) : (
          <Table
            rowKey="id"
            size="small"
            loading={list.isFetching}
            dataSource={list.data?.data ?? []}
            columns={columns}
            locale={{ emptyText: 'No recorded activity yet.' }}
            onRow={(r) => ({ onClick: () => setOpen(r), style: { cursor: 'pointer' } })}
            pagination={{
              current: page,
              pageSize,
              total: list.data?.pagination.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: [25, 50, 100],
              onChange: (p, s) => {
                setPage(s !== pageSize ? 1 : p);
                setPageSize(s);
              },
            }}
          />
        )
      ) : timeline.isError ? (
        <QueryError error={timeline.error} what="the activity timeline" onRetry={() => void timeline.refetch()} />
      ) : timeline.isLoading ? (
        <Spin size="small" />
      ) : (
        <>
          {timeline.data?.truncated && <Alert type="info" showIcon message="Showing the latest 500 events." />}
          <ActivityTimelineView rows={timeline.data?.data ?? []} refs={timeline.data?.refs} onOpen={setOpen} />
        </>
      )}
      <LogDetailDrawer
        row={open}
        refs={view === 'list' ? list.data?.refs : timeline.data?.refs}
        open={!!open}
        onClose={() => setOpen(null)}
      />
    </Space>
  );
}
